import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PlusIcon } from '@heroicons/react/24/outline';
import PageContainer from '../components/layout/PageContainer';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AddCategoryModal from '../components/AddCategoryModal';
import { useAuth } from '../contexts/AuthContext';

// Add Category interface
interface Category {
  id: string;
  name: string;
  emoji: string;
  description: string;
  enabled: boolean;
  docId: string;
}

const CategoryList = () => {
  // Update useState to use Category type
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { user } = useAuth();

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories from API...');
      const response = await fetch('https://getbusinesscategorylist-b7jrylvg3a-uc.a.run.app');
      console.log('API Response status:', response.status);
      const data = await response.json();
      console.log('Received categories:', data);
      setCategories(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading categories:', error);
      setLoading(false);
    }
  };

  const toggleCategoryStatus = async (categoryId: string, currentStatus: boolean) => {
    try {
      const category = categories.find(cat => cat.id === categoryId);
      if (!category) {
        throw new Error('Category not found');
      }

      console.log('Toggling category:', {
        docId: category.docId,
        categoryId: category.docId,
        currentStatus,
        newStatus: !currentStatus
      });

      const response = await fetch('https://editbusinesscategory-b7jrylvg3a-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({
          categoryId: category.docId,
          enabled: !currentStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update category status');
      }

      console.log('Category status updated successfully:', {
        docId: category.docId,
        categoryId: category.docId,
        newStatus: !currentStatus
      });

      // Update local state
      setCategories(prevCategories =>
        prevCategories.map(cat =>
          cat.id === categoryId ? { ...cat, enabled: !currentStatus } : cat
        )
      );
    } catch (error) {
      console.error('Error updating category status:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  // Filter categories based on user access level
  const filteredCategories = user?.accessLevel && user.accessLevel >= 1
    ? categories
    : categories.filter(cat => cat.enabled);

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-white">Categorias</h2>
        {user?.accessLevel && user.accessLevel >= 1 && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Adicionar Nova Categoria
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {filteredCategories.map((category, index) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="relative"
          >
            {user?.accessLevel && user.accessLevel >= 1 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleCategoryStatus(category.id, category.enabled);
                }}
                className={`absolute top-2 right-2 z-10 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                  category.enabled
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                }`}
              >
                {category.enabled ? 'ON' : 'OFF'}
              </button>
            )}
            <Link
              to={`/category/${category.id}`}
              className={`block h-full p-6 backdrop-blur-lg rounded-xl hover:bg-white/20 transition-colors ${
                category.enabled ? 'bg-white/10' : 'bg-white/5 opacity-50'
              }`}
            >
              <div className="flex flex-col items-center text-center">
                <span className="text-4xl mb-4">{category.emoji}</span>
                <h3 className="text-xl font-semibold text-white mb-2">{category.name}</h3>
                <p className="text-white/60">{category.description}</p>
                {!category.enabled && user?.accessLevel && user.accessLevel >= 1 && (
                  <span className="mt-2 text-xs text-white/40">(Desativado)</span>
                )}
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <AddCategoryModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          fetchCategories();
        }}
      />
    </PageContainer>
  );
};

export default CategoryList; 